/* Vocal question builders and their custom question components. */
import { useEffect, useRef, useState } from 'react';
import { audio, type Player } from '../../core/audio';
import { onMic } from '../../core/mic';
import { getSettings, saveRecord, useSettings } from '../../core/store';
import { intervalName, keyFlat, keyName, mod, noteName, noteParts, pcName } from '../../core/theory';
import { choice, fmtDec, type Rng, randInt } from '../../core/util';
import { Btn, PlayButton } from '../../ui/Button';
import { PitchGraph, type PitchGraphHandle } from '../../ui/PitchGraph';
import { Slots, type SlotView } from '../../ui/widgets';
import type { CustomQuestion, HoldQuestion, NotesQuestion, QuestionApi } from '../exercise/types';
import { contour, melody } from '../gitar/melody';
import { tess, vinst } from './voice';

// ---------------------------------------------------------------- hold a note
export function tutQ(lvl: number, rng: Rng): HoldQuestion {
  const t = tess();
  const target = randInt(t.lo, t.hi, rng);
  const tol = [50, 30, 15][lvl - 1] as number;
  const hold = [0.8, 1.2, 2.0][lvl - 1] as number;
  return {
    type: 'hold',
    target,
    tol,
    hold,
    timeout: 12,
    prompt: 'Notayı dinle, sonra söyle ve sabit tut',
    info: `±${tol} sent içinde ${fmtDec(hold)} saniye`,
    play: () => audio.playNote(target, { inst: vinst(), dur: 1.6 }),
    reviewLabel: noteName(target),
    answerText: () => noteName(target),
  };
}

// ---------------------------------------------------------------- scale ladder
export const GAM: Record<number, { name: string; degs: number[]; syl: string }> = {
  1: { name: "5'li gam", degs: [0, 2, 4, 5, 7, 5, 4, 2, 0], syl: 'Mırıldanarak ya da "a" ile' },
  2: { name: 'Oktav gamı', degs: [0, 2, 4, 5, 7, 9, 11, 12, 11, 9, 7, 5, 4, 2, 0], syl: '"a" ya da "u" ile' },
  3: { name: 'Arpej 1-3-5-8', degs: [0, 4, 7, 12, 7, 4, 0], syl: '"ma-me-mi-mo-mu" ile' },
  4: { name: 'Üçlü atlamalar', degs: [0, 4, 2, 5, 4, 7, 5, 2, 0], syl: 'Her notayı net ayırarak' },
};

/** Sing a pattern note by note; each note must be held briefly inside the tolerance. */
function SeqHold({
  api,
  notes,
  tol,
  hold,
  flat,
}: {
  api: QuestionApi;
  notes: number[];
  tol: number;
  hold: number;
  flat: boolean;
}) {
  const label = (m: number) => noteParts(m, { flat });
  const [slots, setSlots] = useState<SlotView[]>(() =>
    notes.map((m): SlotView => ({ main: label(m).main, sub: label(m).sub, cls: '', shake: 0 })),
  );
  const [pos, setPos] = useState(0);
  const [status, setStatus] = useState('Kalıp çaldıktan sonra ilk notadan başla.');
  const graph = useRef<PitchGraphHandle>(null);
  const mic = api.input === 'mic';

  // biome-ignore lint/correctness/useExhaustiveDependencies: one listener per mounted question
  useEffect(() => {
    if (!mic) return;
    graph.current?.setBand(notes[0] as number, tol);
    let p = 0;
    let held = 0;
    let lastT: number | null = null;
    let wrongT = 0;
    const missed = new Set<number>();
    return onMic('frame', (f) => {
      graph.current?.setNow(f.t);
      if (api.isDone()) return;
      if (f.gated) {
        graph.current?.push(f.t, null);
        lastT = null;
        return;
      }
      const dt = lastT == null ? 0 : Math.min(0.1, f.t - lastT);
      lastT = f.t;
      if (!f.voiced || f.rms < f.gate * 1.3 || f.midi == null) {
        graph.current?.push(f.t, null);
        held = Math.max(0, held - dt);
        return;
      }
      const target = notes[p] as number;
      let m = f.midi;
      while (m - target > 6) m -= 12;
      while (target - m > 6) m += 12;
      const cents = (m - target) * 100;
      const ok = Math.abs(cents) <= tol;
      graph.current?.push(f.t, m, ok ? 'good' : Math.abs(cents) <= tol * 2 ? 'warn' : 'bad');
      if (ok) {
        held += dt;
        wrongT = 0;
        if (held >= hold) {
          const done = p;
          setSlots((s) => s.map((x, i) => (i === done ? { ...x, cls: 'ok' } : x)));
          graph.current?.mark(f.t, target, true);
          p++;
          held = 0;
          if (p >= notes.length) {
            setStatus('Bitti!');
            api.done(missed.size === 0, { misses: missed.size });
            return;
          }
          setPos(p);
          graph.current?.setBand(notes[p] as number, tol);
          setStatus(`Sıradaki: ${noteName(notes[p] as number, { flat })}`);
        }
      } else {
        held = Math.max(0, held - dt * 0.5);
        wrongT += dt;
        if (wrongT > 0.9 && !missed.has(p)) {
          missed.add(p);
          api.countAttempt();
          api.feedback(
            `Hedef **${noteName(target, { flat })}**: sen biraz ${cents < 0 ? '**pes**' : '**tiz**'} kaldın (${Math.round(cents)} sent).`,
            'info',
          );
        }
      }
    });
  }, [api]);

  if (!mic)
    return (
      <>
        <Slots slots={slots} current={-1} />
        <div className="selfcheck">
          <p>Kalıbı dinle ve söyle. Mikrofon kapalı olduğu için kendin değerlendir:</p>
          <div className="sc-actions">
            <Btn kind="primary" icon="check" onClick={() => api.done(true, { self: true })}>
              Rahat söyledim
            </Btn>
            <Btn kind="ghost" onClick={() => api.done(false, { self: true })}>
              Zorlandım
            </Btn>
          </div>
        </div>
      </>
    );
  return (
    <>
      <Slots slots={slots} current={pos} />
      <PitchGraph
        ref={graph}
        lo={Math.min(...notes) - 2}
        hi={Math.max(...notes) + 2}
        height={200}
        seconds={6}
        nowFrac={0.9}
      />
      <div className="live-note">{status}</div>
    </>
  );
}

export function gamQ(lvl: number, i: number, base: number): CustomQuestion {
  const g = GAM[lvl] as (typeof GAM)[number];
  const top = Math.max(...g.degs);
  const t = tess();
  const maxTonic = Math.max(t.lo - 2, t.hi + 2 - top);
  let tonic = base + i;
  if (tonic > maxTonic) tonic = Math.max(t.lo - 2, maxTonic - (tonic - maxTonic));
  const notes = g.degs.map((d) => tonic + d);
  const flat = keyFlat(mod(tonic, 12), 'major');
  return {
    type: 'custom',
    prompt: `${g.name}: ${pcName(mod(tonic, 12), { flat })} majör`,
    info: `${g.syl} · önce dinle, sonra söyle`,
    play: () => audio.playNotes(notes, { bpm: 100, inst: vinst() }),
    reviewLabel: `${g.name} (${noteName(tonic, { flat })})`,
    target: notes,
    render: (api) => <SeqHold api={api} notes={notes} tol={40} hold={0.3} flat={flat} />,
  };
}

// ---------------------------------------------------------------- sing an interval
const VA_SETS: Record<number, [number, number][]> = {
  1: [
    [7, 1],
    [12, 1],
  ],
  2: [
    [4, 1],
    [5, 1],
    [7, 1],
  ],
  3: [
    [2, 1],
    [3, 1],
    [4, 1],
    [2, -1],
    [3, -1],
    [4, -1],
  ],
  4: [1, 2, 3, 4, 5, 7, 8, 9, 10, 12].flatMap((i): [number, number][] => [
    [i, 1],
    [i, -1],
  ]),
};

export function vAralikQ(lvl: number, rng: Rng): HoldQuestion {
  const t = tess();
  let iv = 0;
  let dir = 1;
  let root = 0;
  let target = 0;
  let tries = 0;
  do {
    [iv, dir] = choice(VA_SETS[lvl] as [number, number][], rng);
    root = randInt(t.lo, t.hi, rng);
    target = root + dir * iv;
    tries++;
  } while ((target < t.lo - 1 || target > t.hi + 1) && tries < 50);
  return {
    type: 'hold',
    target,
    tol: 35,
    hold: 0.8,
    timeout: 12,
    prompt: `**${intervalName(iv)} ${dir > 0 ? 'yukarı' : 'aşağı'}** söyle`,
    info: 'Başlangıç notası çalar; aralığı sen bul',
    play: () => audio.playNote(root, { inst: vinst(), dur: 1.5 }),
    hints: [
      {
        label: 'Aralığı dinle',
        run: () => {
          audio.playNotes([root, target], { bpm: 70, inst: vinst() });
          return `Hedef nota: **${noteName(target)}**.`;
        },
      },
    ],
    reviewLabel: `${noteName(root)} → ${intervalName(iv)}${dir > 0 ? ' ↑' : ' ↓'}`,
    answerText: () => noteName(target),
  };
}

// ---------------------------------------------------------------- melody echo
export function yankiQ(lvl: number, rng: Rng): NotesQuestion {
  const t = tess();
  const tonicMidi = randInt(t.lo, Math.max(t.lo, t.hi - 7), rng);
  const tonic = mod(tonicMidi, 12);
  const len = [3, 4, 5][lvl - 1] as number;
  const target = melody(rng, {
    tonic,
    scale: 'major',
    len,
    lo: t.lo,
    hi: t.hi,
    maxLeap: [2, 4, 7][lvl - 1],
    stepBias: [0.9, 0.65, 0.5][lvl - 1],
  });
  const flat = keyFlat(tonic, 'major');
  return {
    type: 'notes',
    voice: true,
    target,
    flex: true,
    flat,
    instrument: 'keyboard',
    kb: { from: t.lo - 2, to: t.hi + 2 },
    prompt: 'Melodiyi dinle ve aynısını söyle',
    info: `${len} nota · ${keyName(tonic, 'major')}`,
    play: () => audio.playNotes(target, { bpm: 84, inst: vinst() }),
    playSlow: () => audio.playNotes(target, { bpm: 56, inst: vinst() }),
    hints: [
      { label: 'İlk nota', run: () => `İlk nota: **${noteName(target[0] as number, { flat })}**.` },
      { label: 'Yön', run: () => `Melodinin yönü: ${contour(target)}` },
    ],
    reviewLabel: `${len} notalı melodi`,
    answerText: () => target.map((m) => noteName(m, { flat })).join(' · '),
  };
}

// ---------------------------------------------------------------- siren
/** Three count-in clicks, 0.6 s apart, then the line starts moving (seconds). */
const COUNT_IN = 1.8;

function Siren({
  api,
  curveRel,
  guidePoints,
  center,
  span,
  tol,
  dur,
}: {
  api: QuestionApi;
  curveRel: (x: number) => number;
  guidePoints: [number, number][];
  center: number;
  span: number;
  tol: number;
  dur: number;
}) {
  const graph = useRef<PitchGraphHandle>(null);
  const [status, setStatus] = useState("Hazır olunca Başla'ya bas. Kulaklık takarsan kılavuz sesi de açabilirsin.");
  // the guide never pauses the microphone, so through speakers it would be scored as the singer: headphones only
  const { headphones } = useSettings();
  const [guideOn, setGuideOn] = useState(getSettings().headphones);
  const guide = guideOn && headphones;
  const [started, setStarted] = useState(false);
  const run = useRef({ t0: null as number | null, good: 0, total: 0, running: false, guide: null as Player | null });
  const mic = api.input === 'mic';

  // biome-ignore lint/correctness/useExhaustiveDependencies: one listener per mounted question
  useEffect(() => {
    if (!mic) return;
    const r = run.current;
    graph.current?.setCurve((x) => (r.t0 == null ? curveRel(0) : curveRel(x - r.t0)), tol);
    api.setExplain((_ok, d) =>
      d?.score != null
        ? `Çizginin üzerinde kalma oranın: **%${Math.round(d.score * 100)}**. %55 ve üzeri başarılı sayılır.`
        : '',
    );
    const off = onMic('frame', (f) => {
      graph.current?.setNow(f.t);
      if (!r.running || r.t0 == null) {
        graph.current?.push(f.t, f.voiced ? f.midi : null, null);
        return;
      }
      const x = f.t - r.t0;
      if (x < 0) {
        graph.current?.push(f.t, f.voiced ? f.midi : null, null);
        return;
      }
      if (x > dur) {
        r.running = false;
        const score = r.total ? r.good / r.total : 0;
        setStatus('Bitti.');
        api.done(score >= 0.55, { score });
        return;
      }
      r.total++;
      if (!f.voiced || f.midi == null) {
        graph.current?.push(f.t, null);
        return;
      }
      const target = curveRel(x);
      let m = f.midi;
      while (m - target > 6) m -= 12;
      while (target - m > 6) m += 12;
      const ok = Math.abs(m - target) * 100 <= tol;
      if (ok) r.good++;
      graph.current?.push(f.t, m, ok ? 'good' : Math.abs(m - target) * 100 <= tol * 2 ? 'warn' : 'bad');
      setStatus(`İsabet: %${Math.round((100 * r.good) / r.total)} · kalan ${Math.ceil(dur - x)} sn`);
    });
    return () => {
      off();
      r.guide?.stop();
      r.running = false;
    };
  }, [api]);

  const start = async () => {
    const r = run.current;
    if (r.running) return;
    await audio.resume();
    r.running = true;
    setStarted(true);
    const now = audio.now();
    for (let k = 0; k < 3; k++) audio.drum(k === 0 ? 'clickHi' : 'click', now + (k * COUNT_IN) / 3, 0.7);
    r.t0 = now + COUNT_IN;
    setStatus('3 · 2 · 1 …');
    if (guide) {
      const gp: [number, number][] = [];
      for (let x = 0; x <= dur; x += 0.1) gp.push([x, curveRel(x)]);
      setTimeout(
        () => {
          if (r.running)
            r.guide = audio.play([{ kind: 'glide', t: 0, points: gp, vel: 0.15, noGate: true }], {
              length: dur + 0.2,
              noGate: true,
            });
        },
        (COUNT_IN - 0.05) * 1000,
      );
    }
  };

  if (!mic)
    return (
      <div className="selfcheck">
        <p>
          Bu egzersiz mikrofonla çalışır. Mikrofon olmadan, dinleyerek dudak titreşimiyle ("brrr") yukarı aşağı kayarak
          sesini ısıt:
        </p>
        <PlayButton label="Kılavuz sireni dinle" start={() => audio.playGlide(guidePoints, { vel: 0.18 })} />
        <div className="sc-actions">
          <Btn kind="primary" icon="check" onClick={() => api.done(true, { self: true })}>
            Yaptım
          </Btn>
        </div>
      </div>
    );
  return (
    <>
      <PitchGraph
        ref={graph}
        lo={center - span / 2 - 3}
        hi={center + span / 2 + 3}
        height={230}
        seconds={7}
        nowFrac={0.45}
      />
      <div className="ex-tools-row">
        <Btn kind="primary" icon="play" disabled={started} onClick={start}>
          Başla
        </Btn>
        <label className="check">
          <input
            id="siren-guide"
            type="checkbox"
            checked={guide}
            disabled={!headphones}
            onChange={(e) => setGuideOn(e.target.checked)}
          />
          <span>Kılavuz ses (kulaklıkla)</span>
        </label>
      </div>
      <div className="live-note">{status}</div>
    </>
  );
}

export function sirenQ(lvl: number, rng: Rng): CustomQuestion {
  const t = tess();
  const center = Math.round((t.lo + t.hi) / 2);
  const span = Math.min([5, 8, 12][lvl - 1] as number, t.hi - t.lo);
  const tol = [70, 50, 35][lvl - 1] as number;
  const step = [2.2, 1.7, 1.3][lvl - 1] as number;
  const dur = 16;
  const pts: [number, number][] = [];
  let m = center;
  for (let tt = 0; tt <= dur + step; tt += step) {
    pts.push([tt, m]);
    let next = m;
    let tries = 0;
    do {
      next = Math.round(center - span / 2 + rng() * span);
      tries++;
    } while (Math.abs(next - m) < 2 && span > 3 && tries < 20);
    if (Math.abs(next - m) < 2) next = m + (m < center ? 3 : -3);
    m = next;
  }
  const curveRel = (x: number): number => {
    if (x <= 0) return (pts[0] as [number, number])[1];
    for (let i = 0; i < pts.length - 1; i++) {
      const [t0, m0] = pts[i] as [number, number];
      const [t1, m1] = pts[i + 1] as [number, number];
      if (x >= t0 && x <= t1) {
        const u = (x - t0) / (t1 - t0);
        const e = 0.5 - 0.5 * Math.cos(Math.PI * Math.min(1, u * 1.35));
        return m0 + (m1 - m0) * e;
      }
    }
    return (pts[pts.length - 1] as [number, number])[1];
  };
  return {
    type: 'custom',
    prompt: 'Çizgiyi sesinle takip et',
    info: `±${tol} sent · ${dur} saniye`,
    reviewLabel: 'Siren takibi',
    answerText: () => '',
    // the line every 0.1 s, in seconds after "Başla"
    target: Array.from({ length: Math.round(dur * 10) + 1 }, (_, k): [number, number] => [
      COUNT_IN + k / 10,
      curveRel(k / 10),
    ]),
    render: (api) => (
      <Siren
        api={api}
        curveRel={curveRel}
        guidePoints={pts.filter((p) => p[0] <= dur)}
        center={center}
        span={span}
        tol={tol}
        dur={dur}
      />
    ),
  };
}

// ---------------------------------------------------------------- breath and endurance
function Stopwatch({ goal, api }: { goal: number; api: QuestionApi }) {
  const [t0, setT0] = useState<number | null>(null);
  const [stopped, setStopped] = useState(false);
  const disp = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (t0 == null || stopped) return;
    let raf = 0;
    const tick = () => {
      if (disp.current) disp.current.textContent = `${fmtDec((performance.now() - t0) / 1000, 1)} sn`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [t0, stopped]);
  return (
    <div className="ms-live">
      <div className="ms-note" ref={disp}>
        0,0 sn
      </div>
      <Btn
        kind="primary"
        icon={t0 == null ? 'play' : 'stop'}
        disabled={stopped}
        onClick={() => {
          if (t0 == null) {
            setT0(performance.now());
            return;
          }
          const dur = (performance.now() - t0) / 1000;
          setStopped(true);
          if (disp.current) disp.current.textContent = `${fmtDec(dur, 1)} sn`;
          api.setExplain(() => `Süre: **${fmtDec(dur, 1)} sn**`);
          api.done(dur >= goal, { self: true, dur });
        }}
      >
        {t0 == null ? 'Başlat' : 'Durdur'}
      </Btn>
    </div>
  );
}

function Breath({ api, hiss, goal }: { api: QuestionApi; hiss: boolean; goal: number }) {
  const graph = useRef<PitchGraphHandle>(null);
  const timer = useRef<HTMLDivElement>(null);
  const [sub, setSub] = useState('Başlamak için ses ver');
  const mic = api.input === 'mic';

  // biome-ignore lint/correctness/useExhaustiveDependencies: one listener per mounted question
  useEffect(() => {
    if (!mic) return;
    let start: number | null = null;
    let lastOn: number | null = null;
    const devs: number[] = [];
    let med: number | null = null;
    let ended = false;
    return onMic('frame', (f) => {
      graph.current?.setNow(f.t);
      if (ended || f.gated) return;
      const on = hiss
        ? f.hf > f.gate * 1.6 && f.rms > f.gate && !(f.voiced && f.clarity > 0.85)
        : f.voiced && f.rms > f.gate * 1.3;
      if (on) {
        if (start == null) {
          start = f.t;
          setSub(hiss ? 'Eşit ver…' : 'Sabit tut…');
        }
        lastOn = f.t;
        if (!hiss && f.midi != null) {
          if (med == null) med = f.midi;
          med += (f.midi - med) * 0.05;
          devs.push((f.midi - med) * 100);
          graph.current?.push(f.t, f.midi, Math.abs(f.midi - med) * 100 < 25 ? 'good' : 'warn');
        }
        if (timer.current) timer.current.textContent = `${fmtDec(f.t - start, 1)} sn`;
        return;
      }
      graph.current?.push(f.t, null);
      if (start != null && lastOn != null && f.t - lastOn > (hiss ? 0.45 : 0.4)) {
        ended = true;
        const dur = lastOn - start;
        let stab: number | null = null;
        if (!hiss && devs.length > 10) stab = Math.round(Math.sqrt(devs.reduce((a, b) => a + b * b, 0) / devs.length));
        const rec = saveRecord(hiss ? 'vokal.sss' : 'vokal.sabit', Math.round(dur * 10) / 10);
        api.setExplain(
          () =>
            `Süre: **${fmtDec(dur, 1)} sn**${stab != null ? ` · sapma ±${stab} sent` : ''}${
              rec.better ? ' · yeni rekor!' : rec.prev != null ? ` · rekorun ${fmtDec(rec.prev)} sn` : ''
            }`,
        );
        api.done(dur >= goal, { dur });
      }
    });
  }, [api]);

  if (!mic)
    return (
      <div className="selfcheck">
        <p>Mikrofon kapalı. Süreyi kendin tut: hazır olunca kronometreyi başlat, nefesin bitince durdur.</p>
        <Stopwatch goal={goal} api={api} />
      </div>
    );
  const t = tess();
  return (
    <>
      <div className="ms-live">
        <div className="ms-note" ref={timer}>
          0,0 sn
        </div>
        <div className="ms-detail">{sub}</div>
      </div>
      {hiss ? null : <PitchGraph ref={graph} lo={t.lo - 3} hi={t.hi + 3} height={170} seconds={8} nowFrac={0.92} />}
    </>
  );
}

export function nefesQ(lvl: number): CustomQuestion {
  const hiss = lvl === 2;
  const goal = hiss ? 15 : 10;
  return {
    type: 'custom',
    prompt: hiss
      ? 'Derin nefes al ve "sssss" diye olabildiğince uzun, eşit ver'
      : 'Rahat bir notada "aaa" de ve sesi olabildiğince uzun, sabit tut',
    info: `Hedef: en az ${goal} saniye`,
    reviewLabel: hiss ? '"sss" süresi' : 'Sabit ses',
    answerText: () => '',
    render: (api) => <Breath api={api} hiss={hiss} goal={goal} />,
  };
}
